import type { DateRangePresetKey } from '@/lib/dateRangePresets';

export interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'daterange' | 'range' | 'search';
  allLabel?: string;
  min?: number;
  max?: number;
  /**
   * When true, render a `select` filter as an inline checklist (checkboxes
   * always visible, with +/- negate toggle and Select all/Clear actions),
   * matching legacy `FiltersCheckList::buildFilterCheckList` exactly. When
   * omitted/false, the filter renders as the compact dropdown FilterSelect.
   */
  multi?: boolean;
  /**
   * When true, render a search box at the top of the checklist that filters
   * the visible options client-side. Mirrors legacy `autocomplete` flag in
   * `FiltersCheckList.php`.
   */
  searchable?: boolean;
  /**
   * When true, enables grouped select behavior where options with values
   * starting with "filtersgroup-" act as group parents: checking a parent
   * selects the whole group; unchecking any child expands the group into its
   * remaining members.
   */
  groupSelect?: boolean;
  /**
   * When true (and options carry a `group`), render categories as collapsible
   * headers: the list starts showing only category names, and clicking a
   * category expands its options. Mirrors the legacy Follow That Patient
   * product filter.
   *
   * With `groupSelect` also on, the category header's checkbox selects the
   * group's own `filtersgroup-<id>` option instead of expanding client-side
   * into its children. Use `leadSourceFilterConfig()` rather than hand-rolling
   * this pair.
   */
  collapsibleGroups?: boolean;
  /**
   * For a `daterange` filter: the preset the filter opens on, and the value
   * `clear` returns it to. Omit for the usual unbounded start.
   *
   * A PRESET KEY, not a literal range, and that is the point — the stored value
   * is re-resolved from the key on every load, so a filter defaulting to
   * 'today' means today rather than freezing on whichever day it was first
   * stored. See normalizeDateRanges() in pages/dashboard/dashboardFilters.
   *
   * Giving a list a default range makes 'all' meaningful on it: with a default,
   * an empty range falls back to that default, so asking for the whole history
   * needs an explicit option.
   */
  defaultPreset?: DateRangePresetKey;
}

export interface FilterOption {
  value: string;
  label: string;
  /** Optional group key for rendering non-selectable category headers. */
  group?: string;
}

export type DateRangeValue = { from: string; to: string; preset?: DateRangePresetKey };
export type RangeValue = { min: number; max: number };
export type SelectValue = string[];

export type FilterValue = SelectValue | DateRangeValue | RangeValue;

/**
 * Per-filter negation flag. true == negative (NOT IN / !=) per legacy
 * `filterPlus` semantics. Stored alongside FilterValues.
 */
export type FilterNegations = Record<string, boolean>;

// Guards must be null-safe: stale localStorage values (or partial migrations
// from older filter shapes) can leave `null` where a FilterValue is expected,
// and `'from' in null` throws "right-hand side of 'in' should be an object".
export function isDateRange(v: FilterValue | null | undefined): v is DateRangeValue {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'from' in v;
}

export function isRange(v: FilterValue | null | undefined): v is RangeValue {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'min' in v;
}

export function isSelect(v: FilterValue | null | undefined): v is SelectValue {
  return Array.isArray(v);
}

export function filterActiveCount(v: FilterValue | null | undefined): number {
  if (!v) return 0;
  if (isDateRange(v)) return v.from || v.to ? 1 : 0;
  if (isRange(v)) return v.min > 0 || v.max < 120 ? 1 : 0;
  if (isSelect(v)) return v.length;
  return 0;
}
